import {
  Button,
  Col,
  Layout,
  message,
  Menu,
  Dropdown,
  Row,
  Table,
  Tabs,
  Tag,
} from "antd";
import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import Navbar from "../../Components/Navbar/Navbar";
import PageHeader from "../../Components/PageHeader/PageHeader";
import { ChevronDown, Eye, Pen } from "lucide-react";
import PersistentFooter from "../../Components/PersistentFooter/PersistentFooter";
import { hasRole } from "../../Utils/AccessControl";
import {
  formatNumbers,
  handleExportToExcel,
  handleExportToPDF,
  navigateTo,
  getUserRole,
} from "../../Utils/Common";
import { useTableData } from "../../Utils/useTableData";

const { TabPane } = Tabs;

function renderStatusTag(status: any) {
  const formattedStatus = (status || "").toLowerCase();
  let color = "default";
  let label = status || "Unknown";

  switch (formattedStatus) {
    case "active":
      color = "#34D399";
      label = "Active";
      break;
    case "paid":
      color = "#60A5FA";
      label = "Paid";
      break;
    case "cancelled":
      color = "#F87171";
      label = "Cancelled";
      break;
    case "cancel_request":
      color = "#f99a56ff";
      label = "Cancel Request";
      break;
  }

  return <Tag color={color}>{label}</Tag>;
}

const CreditBillingStatement = () => {
  const [loading,        setLoading]        = useState("");
  const [activeTab,      setActiveTab]      = useState("active");

  const {
    dataSource,
    dataSize,
    loading: tableLoading,
    filterConfig,
    setFilterConfig,
    handleChangePage,
    handlePageSizeChange,
    fetchExport,
  } = useTableData<any>({
    endpoint: "credit_billings/get",
    initialFilters: { page_size: 10, status: "active" },
    isFormData: true,
  });

  function onChangeTab(activeKey: any) {
    setActiveTab(activeKey);
    setFilterConfig((prev: any) => ({ ...prev, status: activeKey, page: 1 }));
  }

  function getBaseColumns() {
    return [
      {
        title: "Billing No.",
        dataIndex: "id",
        key: "id",
      },
      {
        title: "Company Name",
        dataIndex: "company_name",
        key: "company_name",
      },
      {
        title: "Statement No.",
        dataIndex: "statement_no",
        key: "statement_no",
      },
      {
        title: "Amount",
        dataIndex: "2307_calculation",
        key: "2307_calculation",
        align: "right" as const,
        render: (value: any) => <span>{formatNumbers(value)}</span>,
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        render: renderStatusTag,
        width: 130,
      },
    ];
  }

  function getTableColumns() {
    const base = getBaseColumns();

    switch (activeTab) {
      case "active": {
        const activeColumns = [...base];
          activeColumns.push({
            title: "",
            key: "action",
            width: 10,
            render: (_: any, record: any) => (
              <Dropdown
                overlay={
                  <Menu>
                    {!hasRole(getUserRole(), "clerk") && (
                        <Menu.Item
                          key="view"
                          icon={<Eye strokeWidth={1.5} size={15} />}
                          onClick={() => navigateTo("/credit-billing-statement/view/" + record.id)}
                        >
                          View
                        </Menu.Item>
                    )}
                    {hasRole(getUserRole(), "clerk") && (
                      <>
                        <Menu.Item
                          key="review"
                          icon={<Eye strokeWidth={1.5} size={15} />}
                          onClick={() => navigateTo("/credit-billing-statement/review/" + record.id)}
                        >
                          Review
                        </Menu.Item>
                        <Menu.Item
                          key="edit"
                          icon={<Pen strokeWidth={1.5} size={15} />}
                          onClick={() => navigateTo("/credit-billing-statement/edit/" + record.id)}
                        >
                          Edit
                        </Menu.Item>
                      </>
                    )}
                  </Menu>
                }
                trigger={["click"]}
              >
                <Button>Actions <ChevronDown strokeWidth={0.5} /></Button>
              </Dropdown>
            ),
          } as any);
        return activeColumns;
      }

      case "paid": {
        const paidColumns = [...base];
        paidColumns.push({
          title: "",
          key: "action",
          width: 10,
          render: (_: any, record: any) => (
            <Dropdown
              overlay={
                <Menu>
                  <Menu.Item
                    key="view"
                    icon={<Eye strokeWidth={1.5} size={15} />}
                    onClick={() => navigateTo("/credit-billing-statement/view/" + record.id)}
                  >
                    View
                  </Menu.Item>
                </Menu>
              }
              trigger={["click"]}
            >
              <Button>Actions <ChevronDown strokeWidth={0.5} /></Button>
            </Dropdown>
          ),
        } as any);
        return paidColumns;
      }

      case "cancel_request": {
        return [
          ...base,
          {
            title: "Reason",
            dataIndex: "cancel_request_reason",
            key: "cancel_request_reason",
            width: 130,
          },
          {
            title: "",
            key: "action",
            width: 10,
            render: (_: any, record: any) => (
              <Dropdown
                overlay={
                  <Menu>
                    {!hasRole(getUserRole(), "admin") && (
                      <Menu.Item
                        key="view"
                        icon={<Eye strokeWidth={1.5} size={15} />}
                        onClick={() => navigateTo("/credit-billing-statement/view/" + record.id)}
                      >
                        View
                      </Menu.Item>
                    )}
                    {hasRole(getUserRole(), "admin") && (
                      <Menu.Item
                        key="review"
                        icon={<Eye strokeWidth={1.5} size={15} />}
                        onClick={() => navigateTo("/credit-billing-statement/approval/" + record.id)}
                      >
                        Review
                      </Menu.Item>
                    )}
                  </Menu>
                }
                trigger={["click"]}
              >
                <Button>Actions <ChevronDown strokeWidth={0.5} /></Button>
              </Dropdown>
            ),
          },
        ];
      }

      case "cancelled":
        const cancelledColumns = [
          ...base,
          {
            title: "Reason",
            dataIndex: "cancel_request_reason",
            key: "cancel_request_reason",
            width: 130,
          },
        ];
        cancelledColumns.push({
          title: "",
          key: "action",
          width: 10,
          render: (_: any, record: any) => (
            <Dropdown
              overlay={
                <Menu>
                  <Menu.Item
                    key="view"
                    icon={<Eye strokeWidth={1.5} size={15} />}
                    onClick={() => navigateTo("/credit-billing-statement/view/" + record.id)}
                  >
                    View
                  </Menu.Item>
                </Menu>
              }
              trigger={["click"]}
            >
              <Button>Actions <ChevronDown strokeWidth={0.5} /></Button>
            </Dropdown>
          ),
        } as any);
        return cancelledColumns;

      default: {
        const defaultColumns = [...base];
        if (hasRole(getUserRole(), "admin")) {
          defaultColumns.push({
            title: "",
            key: "action",
            width: 10,
            render: (_: any, record: any) => (
              <Dropdown
                overlay={
                  <Menu>
                    <Menu.Item
                      key="view"
                      icon={<Eye strokeWidth={1.5} size={15} />}
                      onClick={() => navigateTo("/credit-billing-statement/view/" + record.id)}
                    >
                      View
                    </Menu.Item>
                  </Menu>
                }
                trigger={["click"]}
              >
                <Button>Actions <ChevronDown strokeWidth={0.5} /></Button>
              </Dropdown>
            ),
          } as any);
        }
        return defaultColumns;
      }
    }
  }

  async function exportToExcel() {
    setLoading("export");
    try {
      const { success, data } = await fetchExport();
      if (success) {
        handleExportToExcel(data, getBaseColumns(), "Credit Billing Statements");
      } else {
        message.error("Failed to fetch data for export.");
      }
    } catch {
      message.error("Error exporting data.");
    } finally {
      setLoading("");
    }
  }

  async function exportToPDF() {
    setLoading("export");
    try {
      const { success, data } = await fetchExport();
      if (success) {
        handleExportToPDF(
          data,
          getBaseColumns(),
          "CreditBillingStatements.pdf",
          { title: "List of Credit Billing Statements", content: `Generated on ${new Date().toLocaleString()}` },
          `Total Records: ${data.length}`
        );
      } else {
        message.error("Failed to fetch data for PDF export.");
      }
    } catch {
      message.error("Error exporting PDF.");
    } finally {
      setLoading("");
    }
  }

  const paginationConfig = {
    current:          filterConfig.page,
    pageSize:         filterConfig.page_size,
    total:            dataSize,
    showSizeChanger:  true,
    onChange:         (page: number) => handleChangePage(page),
    onShowSizeChange: (_: number, size: number) => handlePageSizeChange(size),
  };

  return (
    <Layout className="page">
      <Helmet><title>Agua Lab ERP System</title></Helmet>
      <Navbar />
      <Layout>
        <Row className="px-3 pt-20 md:px-10 md:pb-10 md:pt-8 w-screen md:w-full pb-[50px] sm:pb-0 overflow-scroll text-textColor">
          <Col xs={24}>
            <PageHeader
              title="Credit Billing Statement"
              withDateRangeFilter={false}
              withAddButton={hasRole(getUserRole(), "clerk")}
              addBtnText="Credit Billing Statement"
              onCreate={() => navigateTo("/credit-billing-statement/add")}
              withExportDropdownButton={hasRole(getUserRole(), "clerk")}
              disableExportButton={dataSource?.length === 0}
              onExportToExcel={exportToExcel}
              onExportToPDF={exportToPDF}
              withSearchBtn={false}
              onSearch={(value: any) =>
                setFilterConfig((prev: any) => ({ ...prev, search: value, page: 1 }))
              }
            />
          </Col>

          <Col span={24}>
            <div className="table-container overflow-auto">
              <div className="custom-table min-w-[800px]">
                <Tabs
                  type="card"
                  className="custom-tabs"
                  activeKey={activeTab}
                  onTabClick={onChangeTab}
                >
                  <TabPane tab="Active" key="active">
                    <Table
                      columns={getTableColumns()}
                      dataSource={dataSource}
                      rowKey="id"
                      loading={tableLoading}
                      size="small"
                      pagination={paginationConfig}
                    />
                  </TabPane>
                  <TabPane tab="Paid" key="paid">
                    <Table
                      columns={getTableColumns()}
                      dataSource={dataSource}
                      rowKey="id"
                      loading={tableLoading}
                      size="small"
                      pagination={paginationConfig}
                    />
                  </TabPane>
                  <TabPane tab="Pending Cancel Requests" key="cancel_request">
                    <Table
                      columns={getTableColumns()}
                      dataSource={dataSource}
                      rowKey="id"
                      loading={tableLoading}
                      size="small"
                      pagination={paginationConfig}
                    />
                  </TabPane>
                  <TabPane tab="Cancelled" key="cancelled">
                    <Table
                      columns={getTableColumns()}
                      dataSource={dataSource}
                      rowKey="id"
                      loading={tableLoading}
                      size="small"
                      pagination={paginationConfig}
                    />
                  </TabPane>
                  <TabPane tab="All" key="">
                    <Table
                      columns={getTableColumns()}
                      dataSource={dataSource}
                      rowKey="id"
                      loading={tableLoading}
                      size="small"
                      pagination={paginationConfig}
                    />
                  </TabPane>
                </Tabs>
              </div>
            </div>
          </Col>
        </Row>
      </Layout>

      <PersistentFooter />
    </Layout>
  );
};

export default CreditBillingStatement;